import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { importProvidersFrom } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import pt from '@angular/common/locales/pt';

// Firebase
import { provideFirebaseApp, getApp } from '@angular/fire/app';
import { initializeApp as initializeFirebaseApp, getApps } from 'firebase/app';
import { providePerformance } from '@angular/fire/performance';
import { initializePerformance } from 'firebase/performance';

// NGX Modules
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { FormlyModule } from '@ngx-formly/core';
import { ScriptLoaderModule } from 'ngx-script-loader';
import { HotkeyModule } from 'angular2-hotkeys';
import { QuillModule } from 'ngx-quill';
import { provideNgxStripe } from 'ngx-stripe';

// App modules
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from '@src/environments/environment';
import { ErrorInterceptor } from './app/core/interceptors/error.interceptor';
import { TokenInterceptor } from './app/core/interceptors';
import { AppRepository } from './app/app.repository';
import { AuthRepository } from './app/core/services/auth.repository';
import { AuthService } from './app/core/services/auth.service';
import { ChatService } from './app/core/services/chat/chat.service';
import { PreloadAdminGuard } from './app/core/guards/preload-admin.guard';

// Transloco
import { AppTranslocoModule } from './app/core/transloco/transloco.module';
import { PRELOAD_TRANSLATIONS_PROVIDER } from './app/core/transloco/preload-translations';

// CASL
import { AppAbility, defineAbilitiesFor } from './app/app.ability';
import { AbilityService } from '@casl/angular';

// Register locale data
registerLocaleData(en); // en-US (default)
registerLocaleData(es, 'es-ES'); // Spanish
registerLocaleData(pt, 'pt-BR'); // Portuguese Brazil

if (environment.production) {
  enableProdMode();
}

// AngularFire v21 initializes the Firebase app lazily (only when `FirebaseApp` is
// injected through DI). Our services use the raw Firebase SDK (getAuth/
// onAuthStateChanged), which bypasses DI, so the default app would never be created
// — yielding "No Firebase App '[DEFAULT]' has been created". Initialize it eagerly
// here so any raw SDK call has a default app. `provideFirebaseApp` below reuses it.
if (!getApps().length) {
  initializeFirebaseApp(environment.firebase);
}

function removeLoader() {
  const loader = document.getElementById('app-loader');
  if (loader) {
    loader.style.transition = 'opacity 0.4s ease';
    loader.style.opacity = '0';
    setTimeout(() => loader.remove(), 250); // Smooth removal after fade-out
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    // Router
    provideRouter(routes),

    // Core Angular modules
    importProvidersFrom(
      BrowserAnimationsModule,
      FormsModule,
      ReactiveFormsModule,

      // NGX modules that need .forRoot()
      LoadingBarHttpClientModule,
      LoadingBarRouterModule,
      LoadingBarModule,
      FormlyModule.forRoot(),
      ScriptLoaderModule,
      HotkeyModule.forRoot(),
      QuillModule.forRoot({
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'], // toggled buttons
            ['blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ size: ['small', false] }], // custom dropdown
            [{ color: ['#000000b3', '#cfa95e', '#ff4d4f'] }], // dropdown with defaults
            ['link'],
            ['clean'], // remove formatting button
          ],
        },
      }),

      // Transloco
      AppTranslocoModule,
    ),

    // Firebase — reuse the app initialized eagerly above (avoids double-init).
    // Auth is consumed via the raw SDK (getAuth()), not @angular/fire's `Auth`
    // DI (which is circular under Angular 21 + v1 RC), so no provideAuth here.
    provideFirebaseApp(() => getApp()),
    providePerformance(() =>
      initializePerformance(getApp(), {
        dataCollectionEnabled: true,
        instrumentationEnabled: true,
      }),
    ),

    // Stripe
    provideNgxStripe(environment.stripeKey),

    // HTTP client + DI-based interceptors. `withInterceptorsFromDi()` resolves
    // interceptors lazily per-request instead of at HttpClient construction,
    // which breaks the cycle HttpClient -> HTTP_INTERCEPTORS -> TokenInterceptor
    // -> AuthService -> HttpClient (NG0200 under Angular 21).
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },

    // CASL
    { provide: AppAbility, useFactory: defineAbilitiesFor },
    {
      provide: AbilityService,
      useFactory: (ability: AppAbility) => new AbilityService(ability),
      deps: [AppAbility],
    },

    // App services
    AppRepository,
    AuthRepository,
    AuthService,
    ChatService,
    PreloadAdminGuard,

    // Transloco
    PRELOAD_TRANSLATIONS_PROVIDER,
  ],
})
  .then(() => removeLoader())
  .catch((err) => console.error(err));
